"use client";
import { ImageProvider } from "@/components/ImageProvider/ImageProvider";
import { PostHogProvider } from "@/components/Posthog/PostHogProvider";
import { ThemeProvider } from "@/components/ThemeProvider";
import { UserProvider } from "@/components/UserContext/UserProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import type { UserOptional } from "@/lib/auth";
import type { ConfigType } from "@cardheim/config";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, lazy, useEffect, useState } from "react";

const queryClient = new QueryClient();

const ReactQueryDevtoolsProduction = lazy(() =>
	import("@tanstack/react-query-devtools/build/modern/production.js").then(
		(d) => ({
			default: d.ReactQueryDevtools,
		}),
	),
);

interface Props {
	children: React.ReactNode;
	posthog: ConfigType["posthog"];
	user?: UserOptional;
	imageUrlBase: string;
}

export const Providers: React.FC<Props> = ({
	children,
	posthog,
	user,
	imageUrlBase,
}) => {
	const isDev = process.env.NODE_ENV === "development";
	const [showDevtools, setShowDevtools] = useState(isDev);

	useEffect(() => {
		// @ts-expect-error
		window.toggleDevtools = () => setShowDevtools((old) => !old);
	}, []);

	return (
		<PostHogProvider token={posthog.apiKey} apiHost={posthog.apiHost}>
			<ThemeProvider
				attribute="class"
				defaultTheme="system"
				enableSystem
				disableTransitionOnChange={false}
			>
				<QueryClientProvider client={queryClient}>
					<UserProvider initialUser={user}>
						<ImageProvider imageUrlBase={imageUrlBase}>
							<Suspense fallback={null}>
								<TooltipProvider>{children}</TooltipProvider>
								{showDevtools && (
									<ReactQueryDevtoolsProduction initialIsOpen={false} />
								)}
							</Suspense>
						</ImageProvider>
					</UserProvider>
				</QueryClientProvider>
			</ThemeProvider>
		</PostHogProvider>
	);
};
